/**
 * Layout — App shell with sidebar navigation, header, and content area.
 * Responsive: sidebar collapses to a hamburger menu on mobile.
 */

import { useState, useCallback } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext.tsx';
import { LanguageSwitcher } from './LanguageSwitcher.tsx';

/** Map route paths to translation keys for page titles. */
const getPageTitleKey = (pathname: string): string => {
  if (pathname === '/sites' || pathname === '/') return 'nav.sites';
  if (pathname === '/sites/new') return 'createSite.title';
  if (pathname.match(/^\/sites\/[^/]+\/settings$/)) return 'siteSettings.title';
  if (pathname.startsWith('/sites/')) return 'siteDetail.info.title';
  if (pathname === '/domains') return 'nav.domains';
  return 'nav.dashboard';
};

export const Layout = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const toggleSidebar = useCallback(() => setSidebarOpen((prev) => !prev), []);

  const pageTitle = t(getPageTitleKey(location.pathname));
  const initials = user?.name
    ? user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '?';

  return (
    <div className="app-layout">
      {/* Overlay for mobile sidebar */}
      <div
        className={`sidebar-overlay ${sidebarOpen ? 'sidebar-overlay--visible' : ''}`}
        onClick={closeSidebar}
        aria-hidden="true"
      />

      {/* Sidebar */}
      <aside
        className={`app-sidebar ${sidebarOpen ? 'app-sidebar--visible' : ''}`}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="sidebar__logo">
          <div className="sidebar__logo-icon" aria-hidden="true">S</div>
          <span>{t('nav.appName')}</span>
        </div>

        <nav className="sidebar__nav">
          <NavLink
            to="/sites"
            className={({ isActive }) =>
              `sidebar__nav-item ${isActive || location.pathname === '/' ? 'sidebar__nav-item--active' : ''}`
            }
            onClick={closeSidebar}
          >
            <svg className="sidebar__nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="3" width="7" height="7" />
              <rect x="14" y="3" width="7" height="7" />
              <rect x="3" y="14" width="7" height="7" />
              <rect x="14" y="14" width="7" height="7" />
            </svg>
            {t('nav.sites')}
          </NavLink>

          <NavLink
            to="/domains"
            className={({ isActive }) =>
              `sidebar__nav-item ${isActive ? 'sidebar__nav-item--active' : ''}`
            }
            onClick={closeSidebar}
          >
            <svg className="sidebar__nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <line x1="2" y1="12" x2="22" y2="12" />
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
            {t('nav.domains')}
          </NavLink>
        </nav>

        {user && (
          <div className="sidebar__user">
            <div className="sidebar__user-avatar" aria-hidden="true">
              {initials}
            </div>
            <div className="sidebar__user-info">
              <div className="sidebar__user-name">{user.name || 'User'}</div>
              <div className="sidebar__user-email">{user.email}</div>
            </div>
            <LanguageSwitcher className="language-switcher--sidebar" />
          </div>
        )}
      </aside>

      {/* Main content */}
      <div className="app-main">
        <header className="app-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <button
              className="mobile-menu-btn"
              onClick={toggleSidebar}
              aria-label="Toggle navigation menu"
              aria-expanded={sidebarOpen}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
            <h1 className="app-header__title">{pageTitle}</h1>
          </div>
          <div className="app-header__actions">
            <button className="btn btn--ghost btn--sm" onClick={logout}>
              {t('common.signOut')}
            </button>
          </div>
        </header>

        <main className="app-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
